import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { api } from "../../lib/api";
import ProductCard from "../../components/public/ProductCard";
import { ProductGridSkeleton, ProductCardSkeleton } from "../../components/public/Skeletons";
import { Input } from "../../components/ui/input";
import { Search, ChevronDown, ChevronRight, X, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const trackSearch = (q) => { try { if (q) axios.post(`${BACKEND}/api/shop/analytics/track`, { event: "search", query: q }); } catch {} };

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [cats, setCats] = useState([]);
  const [facets, setFacets] = useState({ brands: [], attributes: [], price: { min: null, max: null } });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [openAttrs, setOpenAttrs] = useState({});
  const [searchInput, setSearchInput] = useState(params.get("q") || "");
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 24;

  const q = params.get("q") || "";
  const category_id = params.get("kategori") || "";
  const brand_id = params.get("marka") || "";
  const attr_name = params.get("ozellik") || "";
  const attr_value = params.get("deger") || "";
  const min_price = params.get("min") || "";
  const max_price = params.get("max") || "";
  const in_showroom = params.get("tesihir") === "1";

  const buildQuery = (extra = {}) => {
    const p = new URLSearchParams();
    const set = (k, v) => v && p.set(k, v);
    set("q", q); set("category_id", category_id); set("brand_id", brand_id);
    set("attr_name", attr_name); set("attr_value", attr_value);
    set("min_price", min_price); set("max_price", max_price);
    if (in_showroom) p.set("in_showroom", "true");
    Object.entries(extra).forEach(([k, v]) => v ? p.set(k, v) : p.delete(k));
    return p.toString();
  };

  // İlk yükleme + filtre değişimi → reset + yeniden yükle
  useEffect(() => {
    (async () => {
      setLoading(true); setSkip(0);
      const [pr, c, f] = await Promise.all([
        api.get(`/shop/products?limit=${PAGE_SIZE}&skip=0&${buildQuery()}`),
        api.get("/shop/categories"),
        api.get(`/shop/facets?${buildQuery()}`),
      ]);
      setItems(pr.data.items || []); setTotal(pr.data.total || 0); setCats(c.data || []);
      setFacets(f.data || { brands: [], attributes: [], price: { min: null, max: null } });
      setLoading(false);
      trackSearch(q);
    })();
    // eslint-disable-next-line
  }, [q, category_id, brand_id, attr_name, attr_value, min_price, max_price, in_showroom]);

  // iter95 — Infinite scroll (IntersectionObserver)
  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    if (items.length >= total) return;
    setLoadingMore(true);
    const nextSkip = skip + PAGE_SIZE;
    try {
      const { data } = await api.get(`/shop/products?limit=${PAGE_SIZE}&skip=${nextSkip}&${buildQuery()}`);
      setItems(prev => [...prev, ...(data.items || [])]);
      setSkip(nextSkip);
    } catch {}
    setLoadingMore(false);
  }, [items.length, total, loading, loadingMore, skip, buildQuery]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [loadMore]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };
  const clearFilters = () => { setSearchInput(""); setParams(new URLSearchParams()); };
  const doSearch = () => setParam("q", searchInput.trim());

  const activeFilters = useMemo(() => {
    const arr = [];
    if (category_id) { const c = cats.find(x => x.id === category_id); if (c) arr.push({ k: "kategori", label: c.name }); }
    if (brand_id) { const b = facets.brands.find(x => x.id === brand_id); if (b) arr.push({ k: "marka", label: b.name }); }
    if (attr_name && attr_value) arr.push({ k: "ozellik", label: `${attr_name}: ${attr_value}`, extra: "deger" });
    if (min_price || max_price) arr.push({ k: "min", label: `${min_price || 0}₺ - ${max_price || "∞"}₺`, extra: "max" });
    if (in_showroom) arr.push({ k: "tesihir", label: "Teşhirde Olanlar" });
    return arr;
  }, [cats, facets, category_id, brand_id, attr_name, attr_value, min_price, max_price, in_showroom]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1">
          <div className="bg-zinc-50 rounded-lg p-4 sticky top-24 space-y-5 max-h-[calc(100vh-6rem)] overflow-y-auto">
            {activeFilters.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-xs uppercase text-zinc-500">Aktif Filtreler</h3>
                  <button onClick={clearFilters} className="text-[10px] text-red-600 hover:underline" data-testid="clear-filters">Temizle</button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {activeFilters.map((f, i) => (
                    <button key={i} onClick={() => { const p = new URLSearchParams(params); p.delete(f.k); if (f.extra) p.delete(f.extra); setParams(p); }}
                            className="text-[11px] bg-white border border-zinc-200 rounded px-2 py-1 flex items-center gap-1 hover:bg-red-50 hover:border-red-300">
                      {f.label} <X size={10}/>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">Kategoriler</h3>
              <div className="space-y-1">
                <button onClick={() => setParam("kategori", "")} className={`w-full text-left px-3 py-1.5 rounded text-sm ${!category_id ? "bg-primary text-white" : "text-zinc-700 hover:bg-zinc-100"}`} data-testid="cat-all">Tümü</button>
                {cats.filter(c => c.product_count > 0).map(c => (
                  <button key={c.id} onClick={() => setParam("kategori", category_id === c.id ? "" : c.id)}
                          className={`w-full text-left px-3 py-1.5 rounded text-sm flex justify-between ${category_id === c.id ? "bg-primary text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
                          data-testid={`cat-${c.id}`}>
                    <span>{c.name}</span><span className="text-xs opacity-70">{c.product_count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">Özel Bölüm</h3>
              <label className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 rounded px-2 py-1">
                <input type="checkbox" checked={in_showroom} onChange={(e) => setParam("tesihir", e.target.checked ? "1" : "")} data-testid="filter-showroom"/>
                <span className="flex-1">Mağazada Teşhirde</span>
              </label>
            </div>

            {facets.brands.length > 0 && (
              <div>
                <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">Marka</h3>
                <div className="space-y-1">
                  {facets.brands.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 rounded px-2 py-1" data-testid={`brand-${b.id}`}>
                      <input type="checkbox" checked={brand_id === b.id} onChange={(e) => setParam("marka", e.target.checked ? b.id : "")}/>
                      <span className="flex-1">{b.name}</span>
                      <span className="text-xs text-zinc-400">{b.count}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {facets.price?.min != null && facets.price?.max != null && facets.price.min !== facets.price.max && (
              <div>
                <h3 className="font-semibold text-xs uppercase text-zinc-500 mb-2">Fiyat (₺)</h3>
                <div className="flex gap-2">
                  <Input type="number" placeholder={`min ${Math.floor(facets.price.min)}`} value={min_price}
                         onChange={(e) => setParam("min", e.target.value)} data-testid="price-min" className="text-xs"/>
                  <Input type="number" placeholder={`max ${Math.ceil(facets.price.max)}`} value={max_price}
                         onChange={(e) => setParam("max", e.target.value)} data-testid="price-max" className="text-xs"/>
                </div>
              </div>
            )}

            {facets.attributes.map(a => {
              const isOpen = openAttrs[a.name] ?? true;
              return (
                <div key={a.name}>
                  <button onClick={() => setOpenAttrs({ ...openAttrs, [a.name]: !isOpen })}
                          className="flex items-center justify-between w-full text-xs uppercase text-zinc-500 font-semibold mb-2">
                    {a.name}
                    {isOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                  </button>
                  {isOpen && (
                    <div className="space-y-1">
                      {a.values.slice(0, 12).map(v => {
                        const active = attr_name === a.name && attr_value === v.value;
                        return (
                          <label key={v.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-100 rounded px-2 py-1" data-testid={`attr-${a.name}-${v.value}`}>
                            <input type="checkbox" checked={active}
                                   onChange={(e) => {
                                     const p = new URLSearchParams(params);
                                     if (e.target.checked) { p.set("ozellik", a.name); p.set("deger", v.value); }
                                     else { p.delete("ozellik"); p.delete("deger"); }
                                     setParams(p);
                                   }}/>
                            <span className="flex-1">{v.value}</span>
                            <span className="text-xs text-zinc-400">{v.count}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h1 className="font-display font-bold text-2xl text-zinc-900">Ürünler</h1>
              <div className="text-sm text-zinc-500">{total} sonuç {q && `— "${q}"`}</div>
            </div>
            <div className="relative w-full md:w-96">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input placeholder="Ürün adı, kod, marka, kategori, özellik..." value={searchInput}
                     onChange={(e) => setSearchInput(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && doSearch()}
                     className="pl-9 pr-16" data-testid="catalog-search" />
              <button onClick={doSearch}
                      className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 rounded bg-primary text-white text-xs font-semibold">
                Ara
              </button>
            </div>
          </div>

          {loading ? <ProductGridSkeleton count={12} cols="grid-cols-2 md:grid-cols-3"/>
            : items.length === 0 ? <div className="p-16 text-center text-zinc-500 bg-zinc-50 rounded-lg">Ürün bulunamadı</div>
            : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="catalog-grid">
                  {items.map(p => <ProductCard key={p.id} p={p} />)}
                </div>
                {items.length < total && (
                  <div ref={sentinelRef} className="py-8" data-testid="catalog-sentinel">
                    {loadingMore
                      ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{Array.from({length: 6}).map((_, i) => <ProductCardSkeleton key={i}/>)}</div>
                      : <div className="text-center text-zinc-400 text-sm">↓ Aşağı kaydırın</div>}
                  </div>
                )}
                {items.length > 0 && items.length >= total && (
                  <div className="py-6 text-center text-xs text-zinc-400">— Tüm ürünler yüklendi ({total}) —</div>
                )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}
